import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RealtimeDBService, getAllUsers, getAllCourses } from '../../firebase/unifiedDataService';
import { useUnifiedAuth } from '../../context/UnifiedAuthContext';
import { USER_ROLES, getRoleDisplayName, getRoleColors } from '../../config/userRoles';
import ViewLayout2 from '../../components/ui/ViewLayout2';
import { Logger } from '../../utils/logger';
import { notifyError, notifySuccess, notifyConfirm } from '../../services/notificationService';

const log = Logger.create('AdminDashboard');

/**
 * AdminDashboard - Main admin control panel
 * Admins can manage all users, see system stats, promote/demote users
 * 
 * DATA SOURCES:
 * - Users: Firestore users/{uid} (via getAllUsers)
 * - Courses: Firestore courses/ (via getAllCourses)
 * - Sessions: RTDB sessions/ (legacy - for active session count)
 */
export default function AdminDashboard() {
  const { user, userProfile, isAdmin, logout, promoteToTeacher, demoteToStudent, deleteUser } = useUnifiedAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState({
    totalUsers: 0,
    admins: 0,
    teachers: 0,
    students: 0,
    totalCourses: 0,
    totalQuizzes: 0,
    totalSessions: 0
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [filterRole, setFilterRole] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Redirect if not admin
  useEffect(() => {
    if (userProfile && !isAdmin) {
      navigate('/dashboard');
    }
  }, [userProfile, isAdmin, navigate]);

  // Load stats and users
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load users from FIRESTORE (single source of truth)
        const usersList = await getAllUsers();
        
        let adminCount = 0;
        let teacherCount = 0;
        let studentCount = 0;
        
        usersList.forEach(u => {
          if (u.role === USER_ROLES.ADMIN) adminCount++;
          else if (u.role === USER_ROLES.TEACHER) teacherCount++;
          else studentCount++;
        });
        
        // Transform Firestore format to expected format (id -> uid)
        const formattedUsers = usersList.map(u => ({
          uid: u.id,
          ...u
        }));
        
        setUsers(formattedUsers);
        
        // Load courses from FIRESTORE
        const courses = await getAllCourses();
        const courseCount = courses.length;
        
        // Active sessions from RTDB using RealtimeDBService
        const sessionsData = await RealtimeDBService.get('sessions');
        let sessionCount = 0;
        if (sessionsData) {
          sessionCount = Object.keys(sessionsData).length;
        }
        
        // Quiz templates from RTDB (legacy count) using RealtimeDBService
        const quizzesData = await RealtimeDBService.get('quiz_templates');
        let quizCount = 0;
        if (quizzesData) {
          Object.values(quizzesData).forEach(userTemplates => {
            if (userTemplates && typeof userTemplates === 'object') {
              quizCount += Object.keys(userTemplates).length;
            }
          });
        }
        
        setStats({
          totalUsers: usersList.length,
          admins: adminCount,
          teachers: teacherCount,
          students: studentCount,
          totalCourses: courseCount,
          totalQuizzes: quizCount,
          totalSessions: sessionCount
        });
        
      } catch (err) {
        log.error('Error loading admin data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  const handlePromote = async (targetUid, targetEmail) => {
    const confirmed = await notifyConfirm(`${targetEmail} zum Lehrer befördern?`, {
      title: 'Benutzer befördern',
      confirmLabel: 'Befördern',
      cancelLabel: 'Abbrechen'
    });
    if (!confirmed) return;
    
    setActionLoading(targetUid);
    try {
      await promoteToTeacher(targetUid);
      setUsers(prev => prev.map(u => 
        u.uid === targetUid ? { ...u, role: USER_ROLES.TEACHER } : u
      ));
      setStats(prev => ({
        ...prev,
        teachers: prev.teachers + 1,
        students: prev.students - 1
      }));
      notifySuccess(`${targetEmail} wurde zum Lehrer befördert`);
    } catch (err) {
      notifyError('Fehler: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDemote = async (targetUid, targetEmail) => {
    const confirmed = await notifyConfirm(`${targetEmail} zum Studierenden zurückstufen?`, {
      title: 'Benutzer zurückstufen',
      confirmLabel: 'Zurückstufen',
      cancelLabel: 'Abbrechen',
      variant: 'warning'
    });
    if (!confirmed) return;
    
    setActionLoading(targetUid);
    try {
      await demoteToStudent(targetUid);
      setUsers(prev => prev.map(u => 
        u.uid === targetUid ? { ...u, role: USER_ROLES.STUDENT } : u
      ));
      setStats(prev => ({
        ...prev,
        teachers: prev.teachers - 1,
        students: prev.students + 1
      }));
      notifySuccess(`${targetEmail} wurde zum Studierenden zurückgestuft`);
    } catch (err) {
      notifyError('Fehler: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (targetUid, targetEmail) => {
    const confirmed = await notifyConfirm(`${targetEmail} PERMANENT löschen?\n\nDies kann nicht rückgängig gemacht werden!`, {
      title: 'Benutzer löschen',
      confirmLabel: 'Endgültig löschen',
      cancelLabel: 'Abbrechen',
      variant: 'danger'
    });
    if (!confirmed) return;
    
    setActionLoading(targetUid);
    try {
      await deleteUser(targetUid);
      // Update local state
      setUsers(prev => prev.filter(u => u.uid !== targetUid));
      // Update stats
      const deletedUser = users.find(u => u.uid === targetUid);
      if (deletedUser) {
        setStats(prev => ({
          ...prev,
          totalUsers: prev.totalUsers - 1,
          admins: deletedUser.role === USER_ROLES.ADMIN ? prev.admins - 1 : prev.admins,
          teachers: deletedUser.role === USER_ROLES.TEACHER ? prev.teachers - 1 : prev.teachers,
          students: deletedUser.role === USER_ROLES.STUDENT ? prev.students - 1 : prev.students
        }));
      }
      notifySuccess(`${targetEmail} wurde gelöscht`);
    } catch (err) {
      notifyError('Fehler: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Filter users
  const filteredUsers = users.filter(u => {
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    const matchesSearch = !searchTerm || 
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.displayName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesRole && matchesSearch;
  });

  if (loading) {
    return (
      <ViewLayout2 title="Admin Dashboard" onBack="/dashboard">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
        </div>
      </ViewLayout2>
    );
  }

  return (
    <ViewLayout2
      title="Admin Dashboard"
      onBack="/dashboard"
      headerActions={[
        {
          label: 'Lehrer-Ansicht',
          onClick: () => navigate('/dashboard'),
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          )
        }
      ]}
    >
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats.totalUsers}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Benutzer gesamt</div>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/30 rounded-xl p-6 shadow-sm border border-purple-200 dark:border-purple-700">
          <div className="text-3xl font-bold text-purple-900 dark:text-purple-200">{stats.admins}</div>
          <div className="text-sm text-purple-600 dark:text-purple-400">Admins</div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-xl p-6 shadow-sm border border-blue-200 dark:border-blue-700">
          <div className="text-3xl font-bold text-blue-900 dark:text-blue-200">{stats.teachers}</div>
          <div className="text-sm text-blue-600 dark:text-blue-400">Lehrende</div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/30 rounded-xl p-6 shadow-sm border border-green-200 dark:border-green-700">
          <div className="text-3xl font-bold text-green-900 dark:text-green-200">{stats.students}</div>
          <div className="text-sm text-green-600 dark:text-green-400">Studierende</div>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalCourses}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Kurse</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalQuizzes}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Quizze</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalSessions}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Sessions</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Admin-Funktionen</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/admin/bug-reports')}
            className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-800 theme-transition"
          >
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="text-left">
              <div className="font-semibold text-red-900 dark:text-red-200">Bug-Reports</div>
              <div className="text-sm text-red-600 dark:text-red-400">Fehlerberichte verwalten</div>
            </div>
          </button>
          {/* Weitere Admin-Funktionen können hier hinzugefügt werden */}
        </div>
      </div>

      {/* User Management */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Benutzerverwaltung</h2>
            
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="text"
                placeholder="Suche nach Name oder E-Mail..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="all">Alle Rollen</option>
                <option value={USER_ROLES.ADMIN}>Admins</option>
                <option value={USER_ROLES.TEACHER}>Lehrende</option>
                <option value={USER_ROLES.STUDENT}>Studierende</option>
              </select>
            </div>
          </div>

          {/* User List */}
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredUsers.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                Keine Benutzer gefunden
              </div>
            ) : (
              filteredUsers.map(u => {
                const colors = getRoleColors(u.role);
                const isCurrentUser = u.uid === user?.uid;
                
                return (
                  <div key={u.uid} className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <div className="flex items-center space-x-4">
                      <div className={`w-10 h-10 ${colors.bg} rounded-full flex items-center justify-center`}>
                        <span className={`text-sm font-medium ${colors.text}`}>
                          {u.displayName?.charAt(0).toUpperCase() || u.email?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {u.displayName || 'Unbekannt'}
                          {isCurrentUser && <span className="text-gray-400 dark:text-gray-500 text-sm ml-2">(Du)</span>}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{u.email}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${colors.bg} ${colors.text}`}>
                        {getRoleDisplayName(u.role)}
                      </span>
                      
                      {!isCurrentUser && (
                        <div className="flex space-x-2">
                          {/* Promote/Demote - nur für nicht-Admins */}
                          {u.role !== USER_ROLES.ADMIN && (
                            <>
                              {u.role === USER_ROLES.STUDENT && (
                                <button
                                  onClick={() => handlePromote(u.uid, u.email)}
                                  disabled={actionLoading === u.uid}
                                  className="px-3 py-1 text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800/50 disabled:opacity-50 theme-transition"
                                  title="Zu Lehrer befördern"
                                >
                                  {actionLoading === u.uid ? '...' : 'Lehrer'}
                                </button>
                              )}
                              {u.role === USER_ROLES.TEACHER && (
                                <button
                                  onClick={() => handleDemote(u.uid, u.email)}
                                  disabled={actionLoading === u.uid}
                                  className="px-3 py-1 text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-800/50 disabled:opacity-50 theme-transition"
                                  title="Zu Student zurückstufen"
                                >
                                  {actionLoading === u.uid ? '...' : 'Student'}
                                </button>
                              )}
                            </>
                          )}
                          
                          {/* Delete Button - für ALLE außer Admins */}
                          {u.role !== USER_ROLES.ADMIN && (
                            <button
                              onClick={() => handleDelete(u.uid, u.email)}
                              disabled={actionLoading === u.uid}
                              className="px-3 py-1 text-xs bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-800/50 disabled:opacity-50 theme-transition flex items-center gap-1"
                              title="Benutzer löschen"
                            >
                              {actionLoading === u.uid ? '...' : (
                                <>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Löschen
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/dashboard"
          className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 theme-transition"
        >
          <div className="text-blue-600 dark:text-blue-400 mb-2">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-white">Kurse verwalten</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Alle Kurse und Quizze</p>
        </Link>
        
        <Link
          to="/llm-generator"
          className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 theme-transition"
        >
          <div className="text-purple-600 dark:text-purple-400 mb-2">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-white">LLM Generator</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">KI-gestützte Fragen</p>
        </Link>
        
        <Link
          to="/settings"
          className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500 theme-transition"
        >
          <div className="text-gray-600 dark:text-gray-400 mb-2">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-white">Einstellungen</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">System konfigurieren</p>
        </Link>
      </div>
    </ViewLayout2>
  );
}
